/**
 * Tag Service - CRUD operations for tags
 */

import { supabase } from '../../supabaseClient';
import { transformTagFromDB, transformTagToDB } from '../utils/transformers';

/**
 * Fetch all tags for current user
 */
export async function fetchAll() {
  try {
    const { data, error } = await supabase
      .from('tags')
      .select('*')
      .is('deleted_at', null)
      .order('name', { ascending: true });

    if (error) throw error;

    return {
      success: true,
      data: data.map(transformTagFromDB)
    };
  } catch (error) {
    console.error('[tagService.fetchAll] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Create a new tag with client_request_id for idempotence
 */
export async function create(tag, clientRequestId = null) {
  const requestId = clientRequestId || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  try {
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No authenticated user');

    const { data, error } = await supabase
      .from('tags')
      .insert([{
        ...transformTagToDB(tag),
        user_id: user.id,
        client_request_id: requestId
      }])
      .select()
      .single();

    if (error) {
      // Check for duplicate (unique violation on client_request_id)
      if (error.code === '23505' && error.message.includes('client_request_id')) {
        console.log('[tagService.create] Duplicate detected, fetching existing tag');

        // Fetch existing tag
        const { data: existing, error: fetchError } = await supabase
          .from('tags')
          .select('*')
          .eq('client_request_id', requestId)
          .single();

        if (fetchError) throw fetchError;

        return {
          success: true,
          data: transformTagFromDB(existing),
          isDuplicate: true
        };
      }

      // Check for unique name constraint
      if (error.code === '23505' && error.message.includes('tags_unique_name_per_user')) {
        return {
          success: false,
          error: 'A tag with this name already exists'
        };
      }

      throw error;
    }

    return {
      success: true,
      data: transformTagFromDB(data)
    };
  } catch (error) {
    console.error('[tagService.create] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Update a tag
 */
export async function update(tagId, updates) {
  try {
    const { data, error } = await supabase
      .from('tags')
      .update(transformTagToDB(updates))
      .eq('id', tagId)
      .select()
      .single();

    if (error) {
      // Check for unique name constraint
      if (error.code === '23505' && error.message.includes('tags_unique_name_per_user')) {
        return {
          success: false,
          error: 'A tag with this name already exists'
        };
      }

      throw error;
    }

    return {
      success: true,
      data: transformTagFromDB(data)
    };
  } catch (error) {
    console.error('[tagService.update] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Soft delete a tag
 */
export async function deleteTag(tagId) {
  try {
    // First, remove all task-tag relationships
    await supabase
      .from('task_tags')
      .delete()
      .eq('tag_id', tagId);

    // Then soft delete the tag
    const { error } = await supabase
      .from('tags')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', tagId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('[tagService.delete] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

export const tagService = {
  fetchAll,
  create,
  update,
  delete: deleteTag
};
